import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import SectionCard from '../../components/admin/SectionCard';
import { LoadingState } from '../../components/admin/StateViews';
import { useToast } from '../../context/ToastContext';
import { ChallengeService } from '../../services/ChallengeService';
import { AdminChallengeFormValues } from '../../types';

const toDateInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

const today = () => new Date().toISOString().slice(0, 10);
const in30Days = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

const empty: AdminChallengeFormValues = {
  title: '',
  description: '',
  causeId: '',
  targetAmount: '',
  startDate: today(),
  endDate: in30Days(),
  coverPhotoUrl: '',
};

const ChallengeFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [values, setValues] = useState<AdminChallengeFormValues>(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const ch = await ChallengeService.getChallengeById(id);
        setValues({
          title: ch.title,
          description: ch.description,
          causeId: ch.causeId,
          targetAmount: Number(ch.targetAmount),
          startDate: toDateInput(ch.startDate),
          endDate: toDateInput(ch.endDate),
          coverPhotoUrl: ch.coverPhotoUrl ?? '',
          status: ch.status,
        });
      } catch {
        setError('Failed to load challenge.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const set = (field: keyof AdminChallengeFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setValues((v) => ({ ...v, [field]: e.target.value }));

  const validate = (): string | null => {
    if (!values.title.trim()) return 'Title is required.';
    if (!values.description.trim()) return 'Description is required.';
    if (!values.causeId.trim()) return 'Cause ID is required.';
    const target = Number(values.targetAmount);
    if (!target || target <= 0) return 'Target amount must be a positive number.';
    if (!values.startDate) return 'Start date is required.';
    if (!values.endDate) return 'End date is required.';
    if (new Date(values.endDate) <= new Date(values.startDate)) return 'End date must be after start date.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isEdit && id) {
        await ChallengeService.updateChallenge(id, values);
        showToast('Challenge updated.', 'success');
        navigate(`/challenges/${id}`);
      } else {
        const created = await ChallengeService.createChallenge(values);
        showToast('Challenge created.', 'success');
        navigate(`/challenges/${created.id}`);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.message)
          ? err.response.data.message.join(', ')
          : null) ||
        'Failed to save challenge.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <AdminPage title={isEdit ? 'Edit Challenge' : 'New Challenge'}>
      <form onSubmit={handleSubmit} noValidate>
        <SectionCard title="Challenge Details">
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Title"
              value={values.title}
              onChange={set('title')}
              required
              fullWidth
              inputProps={{ maxLength: 256 }}
            />

            <TextField
              label="Description"
              value={values.description}
              onChange={set('description')}
              required
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 4096 }}
            />

            <TextField
              label="Cause ID (UUID)"
              value={values.causeId}
              onChange={set('causeId')}
              required
              fullWidth
              placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
            />

            <TextField
              label="Target Amount (EGP)"
              type="number"
              value={values.targetAmount}
              onChange={set('targetAmount')}
              required
              fullWidth
              inputProps={{ min: 0.01, step: 0.01 }}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={values.startDate}
                onChange={set('startDate')}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={values.endDate}
                onChange={set('endDate')}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              label="Cover Photo URL (optional)"
              value={values.coverPhotoUrl ?? ''}
              onChange={set('coverPhotoUrl')}
              fullWidth
              placeholder="https://..."
            />

            {isEdit && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Status
                </Typography>
                <Select
                  size="small"
                  value={values.status ?? 'ACTIVE'}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, status: e.target.value as AdminChallengeFormValues['status'] }))
                  }
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </Box>
            )}
          </Stack>
        </SectionCard>

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
          <Button variant="outlined" onClick={() => navigate(isEdit && id ? `/challenges/${id}` : '/challenges')}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Challenge'}
          </Button>
        </Box>
      </form>
    </AdminPage>
  );
};

export default ChallengeFormPage;
